import { UseFormSetError } from "react-hook-form";
import { QuotationFreightModeOption } from "types/Common";
import { DimensionUnitTypes, QuotationFormStepTypes, WeightUnitTypes } from "constants/";
import { convertWeight, formatNumber, formatWeight, sliceDecimalsWithoutRounding } from "./commonHelpers";

export type BaseWeightUnit = "kg" | "lbs";

export const getBaseWeightUnit = (wUnit?: string | null): BaseWeightUnit => {
  const normalized = typeof wUnit === "string" ? wUnit.toLowerCase() : "";
  return normalized.includes("lbs") ? WeightUnitTypes.LBS : WeightUnitTypes.KG;
};

export const isTotalWeightUnit = (wUnit?: string | null): boolean =>
  typeof wUnit === "string" && wUnit.toLowerCase().includes("total");

/**
 * Per-line total weight in the item's own unit (no cross-unit conversion).
 * Supports API `weight` and form `weight_piece`, plus total_kg / total_lbs.
 */
export const getItemTotalWeight = (item: {
  weight?: number | string | null;
  weight_piece?: number | string | null;
  units?: number | string | null;
  w_unit?: string | null;
}): number => {
  if (!item) return 0;
  const weight = Number(item.weight_piece ?? item.weight ?? 0);
  const units = Number(item.units ?? 0);
  if (!weight || Number.isNaN(weight)) return 0;
  if (isTotalWeightUnit(item.w_unit)) return weight;
  return weight * (units || 1);
};

/**
 * Sum packing-list / good-item weights after converting each line to a common unit.
 */
export const calculateGoodItemsTotalWeight = (
  goodItems: any[] | undefined | null,
  targetUnit?: BaseWeightUnit,
): { weight: number; unit: BaseWeightUnit } => {
  const items = Array.isArray(goodItems) ? goodItems.filter(Boolean) : [];
  if (!items.length) {
    return { weight: 0, unit: targetUnit ?? WeightUnitTypes.KG };
  }

  const unit = targetUnit ?? getBaseWeightUnit(items[0]?.w_unit);

  const weight = items.reduce((acc, item) => {
    let itemWeight = getItemTotalWeight(item);
    const fromUnit = getBaseWeightUnit(item.w_unit);
    if (fromUnit !== unit && itemWeight) {
      itemWeight = parseFloat(convertWeight(itemWeight, { fromUnit, toUnit: unit }));
    }
    return acc + (Number.isFinite(itemWeight) ? itemWeight : 0);
  }, 0);

  return { weight, unit };
};

export type DraftDataKeys = "additional_information" | "costs" | "schedule" | "service" | "service_confirmation";

export type DraftDataType = Record<DraftDataKeys, Record<string, any>>;

export interface CustomServiceConfirmationQuestion {
  question_pk: number;
  question_type: "multiple_choices" | "yes/no" | "free_text";
  question: string;
  allow_multiple_answers: boolean;
  multiple_option_choices?: string[];
}

export const SERVICE_REQUIREMENT_LABEL_MAPPING: Record<string, string> = {
  dtd: "Door to Door",
  dtp: "Door to Port",
  ptd: "Port to Door",
  ptp: "Port to Port",
  export_clearance: "Export clearance",
  transit_clearance: "Transit clearance",
  import_clearance: "Import clearance",
};

export const modifyDraftData = (data: DraftDataType) => {
  if (!data) return;
  modifyCostSectionData(data);

  // Modify the service_confirmation.defaults object to prefill radio buttons
  if (data?.service_confirmation?.defaults) {
    Object.keys(data.service_confirmation.defaults).forEach((key: string) => {
      const value = data.service_confirmation.defaults[key]?.value?.toString();
      if (value) {
        data.service_confirmation.defaults[key].value = value;
      }
    });
  }
  if (data?.service_confirmation && !data?.service_confirmation?.custom) {
    data.service_confirmation.custom = [];
  }

  Object.entries(data.costs).forEach(([key, costs]: [string, any]) => {
    if (costs?.length === 0 || !Array.isArray(costs)) return;
    data.costs[key] = costs.map((cost: any) => ({
      ...cost,
      amount: sliceDecimalsWithoutRounding(cost?.amount || 0),
      quantity: sliceDecimalsWithoutRounding(cost?.quantity || 0),
    }));
  });
  return data;
};

/**
 * Strip schedule.stops from a load-template / duplicate draft so foreign
 * stop_ids from another shipment never enter the create-quotation form.
 * Leaves flights, vessels, and RFQ deadline intact.
 */
export const omitScheduleStops = <T extends { schedule?: Record<string, any> | null }>(data: T): T => {
  if (!data?.schedule) return data;
  const { stops: _omittedStops, ...scheduleWithoutStops } = data.schedule;
  return {
    ...data,
    schedule: scheduleWithoutStops,
  };
};

const modifyCostSectionData = (data: DraftDataType) => {
  if (!data?.costs) {
    data.costs = {};
  }
  if (!data.costs?.main_freight_charges) {
    data.costs.main_freight_charges = [];
  }
  data.costs.other_charges = [
    ...(data.costs.origin_charges || []).map((item: any) => ({
      ...item,
      applies_to: "origin",
    })),
    ...(data.costs.destination_charges || []).map((item: any) => ({
      ...item,
      applies_to: "destination",
    })),
  ];
  delete data.costs.origin_charges;
  delete data.costs.destination_charges;
};

export const parseDraftValues = (
  data: any,
  config?: any,
  quoteRequestStops?: Array<{ stop_id?: string | number | null }> | null,
) => {
  // deep cloning so orignal RHF state don't get mutated.
  const clonedData = JSON.parse(JSON.stringify(data));

  // if custom service confirmation is not allowed for the selected freight mode, clear the custom service confirmation
  const selectedFreightMode = clonedData?.service?.freight_mode;
  if (
    config &&
    !config?.custom_service_confirmation?.[selectedFreightMode] &&
    clonedData.service_confirmation?.custom?.length > 0
  ) {
    clonedData.service_confirmation.custom = [];
  }

  if (clonedData?.costs?.other_charges?.length) {
    clonedData.costs.origin_charges = [];
    clonedData.costs.destination_charges = [];
    clonedData.costs.other_charges.forEach((item: any) => {
      if (item?.applies_to === "origin") {
        clonedData.costs.origin_charges.push(item);
      } else {
        clonedData.costs.destination_charges.push(item);
      }
    });
    delete clonedData.costs.other_charges;
  }

  // Bind stop_id to the current quote-request stops so template/foreign IDs never submit.
  if (Array.isArray(quoteRequestStops) && Array.isArray(clonedData?.schedule?.stops)) {
    clonedData.schedule.stops = clonedData.schedule.stops.map((stop: any, index: number) => {
      const ownedStopId = quoteRequestStops[index]?.stop_id;
      if (ownedStopId === undefined || ownedStopId === null || ownedStopId === "") {
        return { ...stop, stop_id: "" };
      }
      return { ...stop, stop_id: ownedStopId };
    });
  }

  return clonedData;
};

export const resolveServerErrors = (error: any, setError: UseFormSetError<any>) => {
  if (!error?.response?.data || typeof error?.response?.data !== "object") return;
  const data = JSON.parse(JSON.stringify(error.response.data));
  const entries = Object.entries(data);
  if (entries.length === 0) return;
  entries.forEach(([key]) => {
    const section = key.split(".")[0];
    if (section) setError(section, { type: "server", message: data[key] });
  });
};

export const getPackingListCalculations = (quoteRequestDetails: any) => {
  // listItem is a string array so we can use .join(", ")
  const goodItems: any[] = quoteRequestDetails?.organization?.good_items;
  if (!goodItems) return;

  const { weight: totalWeightValue, unit: weightUnit } = calculateGoodItemsTotalWeight(goodItems);

  const accumulatedAttributes = goodItems.reduce<{
    totalCBM: number;
    listItems: string[];
  }>(
    (accumulated, item) => ({
      totalCBM: accumulated.totalCBM + (item?.total_cbm ?? 0),
      listItems: [...accumulated.listItems, `${item?.units} ${item.handling_unit}`],
    }),
    { totalCBM: 0, listItems: [] },
  );

  const { totalCBM, listItems } = accumulatedAttributes;
  const totalDimensionalWeight = getDimensionalWeight(goodItems, weightUnit);

  return {
    totalWeight: formatWeight(totalWeightValue),
    totalCBM: formatNumber(totalCBM),
    totalDimensionalWeight: formatNumber(totalDimensionalWeight),
    listItems,
    weightUnit,
  };
};

export const prefillChargeCodes = (charges: any, customChargeCodes: any, defaultCurrency: string) => {
  const existingCharges: any = {};
  const existingAdditionalCharges: any = [];
  let newCharges = [];
  charges.forEach((charge: any) => {
    if (charge.custom_charge_code_id) {
      if (!existingCharges[charge.custom_charge_code_id]) {
        existingCharges[charge.custom_charge_code_id] = charge;
      } else if (Array.isArray(existingCharges[charge.custom_charge_code_id])) {
        existingCharges[charge.custom_charge_code_id].push(charge);
      } else {
        existingCharges[charge.custom_charge_code_id] = [existingCharges[charge.custom_charge_code_id], charge];
      }
    } else {
      existingAdditionalCharges.push(charge);
    }
  });
  if (customChargeCodes?.length) {
    newCharges.push(
      ...(customChargeCodes?.map((charge: any) => {
        if (Array.isArray(existingCharges[charge?.id])) {
          newCharges.push(...existingCharges[charge.id]);
          return undefined;
        }
        return {
          description: `${charge.charge_code}${charge.charge_code && charge.charge_label ? " - " : ""}${
            charge.charge_label
          }`,
          measurement_unit: existingCharges[charge?.id]?.measurement_unit || "",
          amount: existingCharges[charge?.id]?.amount || "",
          quantity: existingCharges[charge?.id]?.quantity || "",
          currency_code: existingCharges[charge?.id]?.currency_code || defaultCurrency,
          custom_charge_code_id: charge.id,
          ...(charge.applies_to && { applies_to: charge.applies_to }),
        };
      }) || []),
    );
    newCharges = newCharges.filter(Boolean);
  }
  newCharges.push(...existingAdditionalCharges);
  return newCharges;
};

export const getServiceInputVisibility = (selectedFreightModeObject?: QuotationFreightModeOption) => {
  if (!selectedFreightModeObject) return {};

  const isRoadFreight = selectedFreightModeObject.value === "road_freight";

  const showChargeableWeight =
    ["regular_air", "nfo"].includes(selectedFreightModeObject.value) ||
    (selectedFreightModeObject?.is_custom_freight && selectedFreightModeObject.type === "air");
  const showRoadFreightType = isRoadFreight;
  const showMaxDimensions = isRoadFreight;
  const showMaxWeight = isRoadFreight;
  const showMaxAllowedWeight = ["obc"].includes(selectedFreightModeObject.value);

  return { showChargeableWeight, showRoadFreightType, showMaxDimensions, showMaxWeight, showMaxAllowedWeight };
};

export const getFirstSectionWithErrors = (sectionErrorKeys: string[]) => {
  if (sectionErrorKeys.includes("service")) return QuotationFormStepTypes.SERVICE;
  if (sectionErrorKeys.includes("costs")) return QuotationFormStepTypes.COSTS;
  if (sectionErrorKeys.includes("schedule")) return QuotationFormStepTypes.SCHEDULE;
  if (sectionErrorKeys.includes("additional_information")) return QuotationFormStepTypes.MISC;
  if (sectionErrorKeys.includes("service_confirmation")) return QuotationFormStepTypes.MISC;
  return null;
};

export const getDimensionalWeight = (goodItems: any, targetUnit?: BaseWeightUnit) => {
  if (!goodItems || !Array.isArray(goodItems)) {
    return 0;
  }

  // Always compute dimensional weight in kg (dims converted to cm, divisor 6000),
  // then convert once to the target unit so mixed packing-list units stay consistent.
  let totalDimensionalWeightKg = 0;

  goodItems.filter(Boolean).forEach((goodItem) => {
    if (goodItem.width && goodItem.height && goodItem.length && goodItem.units && goodItem.m_unit) {
      let width = parseFloat(goodItem.width);
      let height = parseFloat(goodItem.height);
      let length = parseFloat(goodItem.length);
      const units = parseInt(goodItem.units, 10);

      if (String(goodItem.m_unit).toLowerCase() === DimensionUnitTypes.INCH) {
        width = +(width * 2.54).toFixed(2);
        height = +(height * 2.54).toFixed(2);
        length = +(length * 2.54).toFixed(2);
      }

      totalDimensionalWeightKg += (width * height * length * units) / 6000;
    }
  });

  const unit = targetUnit ?? getBaseWeightUnit(goodItems.find(Boolean)?.w_unit);
  if (unit === WeightUnitTypes.LBS) {
    return +parseFloat(
      convertWeight(totalDimensionalWeightKg, { fromUnit: WeightUnitTypes.KG, toUnit: WeightUnitTypes.LBS }),
    ).toFixed(2);
  }

  return +totalDimensionalWeightKg.toFixed(2);
};

export const getChargeableWeight = (
  totalWeight: number,
  totalDimensionalWeight: number,
  options?: {
    inputUnit?: "lbs" | "kg";
    outputUnit?: "lbs" | "kg";
  },
): number => {
  const { inputUnit, outputUnit } = options ?? {};
  const weight = Math.max(totalWeight, totalDimensionalWeight);

  if (!options || !inputUnit || !outputUnit || inputUnit === outputUnit) return weight;
  if (outputUnit === "lbs") return Number((weight * 2.20462).toFixed(2));
  if (outputUnit === "kg") return Number((weight / 2.20462).toFixed(2));
  return weight;
};
